import { addDays } from 'date-fns'
import { MaybeUserInfo, MembershipFeesQueryType } from '../../../../types'
import {
  formatBasicDateStr,
  formatDate,
  isBeforeDate,
  StringOrNumber
} from '../../../../utils'
import { ACCRUAL_BASE_TYPE_EUROS } from '../../constants'
import {
  DEFAULT_REFERENCE_NUMBER,
  DEFAULT_REFERENCE_PAYMENT_TYPE
} from '../constants'
import {
  CalculateMembershipFeeInput,
  VirtualBarCodeOptionsType
} from '../types'

const getDueDate = (endDay: string) => {
  const endDate = new Date(endDay)
  return addDays(endDate, 15).toISOString()
}

const getDueDateForVirtualBarCode = (endDay: string) => {
  const dueDate = getDueDate(endDay)
  return formatDate(dueDate, 'yyMMdd')
}

export const parseNumbersWithCommas = (numberWithComma: StringOrNumber) => {
  const parsedNumber = parseFloat(numberWithComma.toString().replace(',', '.'))
  return parsedNumber
}

export const makeDueDate = (variables: CalculateMembershipFeeInput) => {
  let due = null
  const dueDate = getDueDate(variables.endDate)
  const today = new Date()

  if (!isBeforeDate(today, dueDate)) {
    due = formatBasicDateStr(dueDate)
  }
  return due
}

export const getVirtualBarCode = (
  variables: CalculateMembershipFeeInput,
  user: MaybeUserInfo,
  bankAccounts: MembershipFeesQueryType['bankAccounts']
) => {
  let barcode = null
  const due = getDueDateForVirtualBarCode(variables.endDate)
  const reference = getReferenceNumber(variables, user)
  const cents = getCents(variables)
  const iban = bankAccounts?.[0]?.iban

  if (reference && iban) {
    const options = {
      due,
      reference,
      cents,
      iban
    }

    barcode = makeVirtualBarcode(options)
  }
  return barcode
}

export const parseAmount = (variables: CalculateMembershipFeeInput) => {
  const { amount, grossSalary, type } = variables

  const parsedAmount = parseNumbersWithCommas(amount)
  const amountForType =
    type === ACCRUAL_BASE_TYPE_EUROS
      ? parsedAmount
      : parsedAmount * 0.01 * parseNumbersWithCommas(grossSalary)

  return amountForType
}

export const getCents = (variables: CalculateMembershipFeeInput) => {
  const cents = parseAmount(variables)

  const formattedCents = Math.round(cents * 100)

  return formattedCents
}

export const getReferenceNumber = (
  variables: CalculateMembershipFeeInput,
  user: MaybeUserInfo
) => {
  let memberNumber = user?.jasennumero?.toString()

  while (memberNumber && memberNumber.length < 7) {
    memberNumber = '0' + memberNumber
  }

  const startDate = formatDate(variables.startDate, 'ddMM')
  const endDate = formatDate(variables.endDate, 'ddMMyy')

  let referenceNumber = null

  if (memberNumber) {
    const referenceWithOutCheckMark = DEFAULT_REFERENCE_NUMBER.concat(
      memberNumber,
      startDate,
      endDate,
      DEFAULT_REFERENCE_PAYMENT_TYPE
    )

    const checkMark = getReferenceCheckMark(referenceWithOutCheckMark)

    referenceNumber = referenceWithOutCheckMark.concat(checkMark)
  }
  return referenceNumber
}

export const getReferenceCheckMark = (referenceWithOutCheckMark: string) => {
  const emphasis = [7, 3, 1]
  const reversedArr = referenceWithOutCheckMark.split('').reverse()

  let multiplication = 0
  let multiplicationSum = 0

  for (let i = 0; i < referenceWithOutCheckMark.length; i++) {
    multiplication = emphasis[i % 3] * parseInt(reversedArr[i])
    multiplicationSum += multiplication
  }
  const lastDigit = multiplicationSum % 10
  const nextFullTen = multiplicationSum + 10 - lastDigit
  const difference = nextFullTen - multiplicationSum

  const checkMark = difference === 10 ? 0 : difference

  return checkMark.toString()
}

export const makeVirtualBarcode = (options: VirtualBarCodeOptionsType) => {
  const formatted: Record<string, StringOrNumber> = {}

  formatted.iban = convertIBAN(options.iban)

  formatted.reference = convertReference(options.reference)
  formatted.version = referenceToVersion(options.reference)

  formatted.amount = convertAmountOfCents(options.cents)

  formatted.due = checkDue(options.due)

  const barcode =
    formatted.version +
    formatted.iban +
    formatted.amount +
    formatted.reference +
    formatted.due

  return barcode
}

// Different versions of Virtuaaliviivakoodi
const VERSION_NATIONAL = 4
const VERSION_INTERNATIONAL = 5

const checkDue = (due: string) => {
  const pattern = /^[0-9]{6}$/

  if (!pattern.test(due)) {
    throw new Error('Due date is not valid')
  }

  return due
}

const NON_ALPHANUM = /[^a-zA-Z0-9]/g

const electronicFormat = (iban: string) => {
  return iban.replace(NON_ALPHANUM, '').toUpperCase()
}

/**
 * Convert IBAN account number to suitable format.
 * @param {String} iban
 * @returns {String} Converted IBAN
 */
const convertIBAN = (iban: string) => {
  const pattern = /^FI/

  if (!pattern.test(iban)) {
    throw new Error('Given IBAN is not Finnish')
  }

  // Return electronic format IBAN with country code removed
  return electronicFormat(iban).substring(2)
}

/**
 * Convert reference number to sufficiently padded format.
 * Defines reference's type automatically.
 * @param {String} reference
 * @returns {String} Converted reference number
 */
const convertReference = (reference: string) => {
  // remove any whitespace from the reference
  reference = reference.replace(/\s/g, '')

  // Determine how to pad the reference from the version
  const version = referenceToVersion(reference)

  // In case of international reference we pad everything after checksum
  if (version === VERSION_INTERNATIONAL) {
    reference = reference.substring(2, 4) + pad(reference.substring(4), 21)
  }

  return pad(reference, 23)
}

/**
 * Determine Virtuaaliviivakoodi's version by given reference number.
 * Return values are 5 = international reference and 4 = national type.
 * @param {String} reference - Reference number
 * @returns {Number} The version
 */
const referenceToVersion = (reference: string) => {
  // remove any whitespace from the reference
  const referenceString = reference.replace(/\s/g, '')

  const internationalPattern = /^RF[0-9]{3,23}$/
  const nationalPattern = /^[0-9]{4,20}$/

  if (internationalPattern.test(referenceString)) {
    return VERSION_INTERNATIONAL
  }
  if (nationalPattern.test(referenceString)) {
    return VERSION_NATIONAL
  }
  throw new Error('Given reference is not valid')
}

/**
 * Converts given amount of cents to 8 characters long string.
 * The amount can't be negative or bigger than 99999999.
 * @param {Number} cents
 * @returns {String} Padded amount string
 */
const convertAmountOfCents = (cents: number) => {
  // Check that cents is an integer
  const isInteger = countDecimals(cents) === 0
  if (!isInteger) {
    throw new Error('Given amount is not an integer')
  }

  // Return string of padded cents
  return pad(cents.toString(), 8)
}

/**
 * Counts number of decimals
 * @param {Number} number
 * @returns {Number} Number of decimals
 */
const countDecimals = (num: number) => {
  // Check that the argument is a number
  if (typeof num !== 'number' || isNaN(num)) {
    throw new Error('Given parameter is not a number')
  }

  if (Math.floor(num) === num) return 0
  return String(num).split('.')[1].length
}

/**
 * Pads values to fixed width with zero.
 * @param {String} value
 * @param {Number} width
 * @returns {String}
 */
const pad = (value: string, width: number) => {
  if (value.length > width)
    throw new Error(
      `Value is ${value.length} characters long, maximum being ${width}`
    )

  return new Array(width - value.length + 1).join('0') + value
}
